// Sprint 11b.2 §2.3 — Wraps a WorkspaceAvatar with a context menu.
// DS.5 (sprint aos-ds-shell, spec §2 D3) — the menu is the DS `Menu` through
// ds/ContextMenuHost.tsx (a fixed anchor at the pointer; the piece has no
// contextmenu binding of its own). `ui/context-menu.tsx`'s last importer.
//
// Right-click (desktop) opens the menu at the pointer. Mobile devices have
// no right-click — we synthesize a `contextmenu` event at the touch position
// after a 600 ms hold (movement ≤ 5 px). Below 600 ms or with movement
// the dnd-kit TouchSensor (400 ms + 5 px tolerance, see WorkspaceColumn)
// takes over for drag-to-reorder; above 600 ms with no movement, this
// long-press handler wins.
//
// Menu items:
//   * "Edit project…" — opens EditProjectModal via `onEdit`.
//   * "Archive"       — calls `onArchive` (existing archive endpoint).

import { useEffect, useRef, useState } from 'react';
import { ContextMenuHost } from './ds/ContextMenuHost';
import { useTranslation } from '../i18n';

const LONG_PRESS_MS = 600;
const MOVEMENT_TOLERANCE_PX = 5;

export interface WorkspaceAvatarContextMenuProps {
  children: React.ReactNode;
  onEdit: () => void;
  onArchive: () => void;
  /** Disable the menu (e.g., for the default project that cannot be archived). */
  archiveDisabled?: boolean;
}

export function WorkspaceAvatarContextMenu({
  children,
  onEdit,
  onArchive,
  archiveDisabled,
}: WorkspaceAvatarContextMenuProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);

  // Cleanup on unmount so a pending long-press timer doesn't fire after
  // the avatar is removed.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  function clearTimer() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    // Only act on touch / pen — mouse uses native right-click.
    if (e.pointerType === 'mouse') return;
    firedRef.current = false;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    clearTimer();
    timerRef.current = setTimeout(() => {
      // Fire only if the pointer hasn't moved enough to trigger drag.
      // The dnd-kit TouchSensor activates at 400ms with movement > 5px;
      // we wait until 600ms to give it priority on movement-based gestures.
      if (firedRef.current) return;
      const target = triggerRef.current;
      if (!target) return;
      const ev = new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: startPosRef.current?.x ?? 0,
        clientY: startPosRef.current?.y ?? 0,
      });
      firedRef.current = true;
      target.dispatchEvent(ev);
    }, LONG_PRESS_MS);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!startPosRef.current) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    if (dx > MOVEMENT_TOLERANCE_PX || dy > MOVEMENT_TOLERANCE_PX) {
      clearTimer();
    }
  }

  function onPointerEnd() {
    clearTimer();
    startPosRef.current = null;
  }

  function onContextMenu(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    setMenuAt({ x: e.clientX, y: e.clientY });
  }

  return (
    <>
      <div
        ref={triggerRef}
        onContextMenu={onContextMenu}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onPointerLeave={onPointerEnd}
        data-testid="workspace-avatar-context-trigger"
      >
        {children}
      </div>
      <ContextMenuHost
        open={menuAt !== null}
        position={menuAt}
        onClose={() => setMenuAt(null)}
        items={[
          {
            label: <span data-testid="workspace-context-edit">{t('workspaceCustomization.edit')}</span>,
            onClick: onEdit,
          },
          {
            label: (
              <span data-testid="workspace-context-archive">{t('workspaceCustomization.archive')}</span>
            ),
            onClick: onArchive,
            disabled: archiveDisabled,
          },
        ]}
      />
    </>
  );
}
